import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, LayoutChangeEvent, Platform, View } from 'react-native';

import { IconSearch, Pressable, createMqStyles, tokens } from '@fhs/ui';

import { LocationSearchBar } from './location-search-bar';
import { LocationSearchBarHandle } from './types';

/** The design draws the collapsed control as a 48pt circle at the top-right of the map. */
const CIRCLE_SIZE = 48;
/** The circle hangs 17pt past the field's right edge, so it covers 31pt of the field. */
const CIRCLE_OVERHANG = 17;
const CIRCLE_OVERLAP = CIRCLE_SIZE - CIRCLE_OVERHANG;
const EXPAND_DURATION = 1000;

interface MapSearchOverlayProps {
  /**
   * Bumped whenever the guest touches something outside the field — the map, a pin, a map
   * control — so the field gives up focus and takes the keyboard with it.
   */
  dismissSignal: number;
  /**
   * Bumped when locate-me replaces a searched location, so the field empties and tucks
   * back behind the magnifier rather than keeping the old query on screen.
   */
  clearSignal: number;
  onSelectLocation?: () => void;
}

/**
 * On mobile and native the map takes the full width, so the address input collapses to a
 * magnifier that expands into a floating input over the map rather than consuming a row of the
 * panel.
 *
 * Per the design the magnifier circle never leaves: the field slides out from behind it and
 * tucks its right edge underneath, so the circle keeps reading as the field's search affordance.
 */
export const MapSearchOverlay: React.VFC<MapSearchOverlayProps> = ({
  dismissSignal,
  clearSignal,
  onSelectLocation,
}) => {
  const styles = useMapSearchOverlayMqStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  // Keeping the field unmounted while collapsed stops it from stealing taps meant for the map.
  const [isFieldMounted, setIsFieldMounted] = useState(false);
  const [availableWidth, setAvailableWidth] = useState(0);
  const progress = useRef(new Animated.Value(0)).current;
  const fieldRef = useRef<LocationSearchBarHandle | null>(null);

  useEffect(() => {
    if (isExpanded) {
      setIsFieldMounted(true);
    }

    Animated.timing(progress, {
      toValue: isExpanded ? 1 : 0,
      duration: EXPAND_DURATION,
      easing: Easing.out(Easing.cubic),
      // Width is not supported by the native driver.
      useNativeDriver: false,
    }).start(({ finished }) => {
      if (finished && !isExpanded) {
        setIsFieldMounted(false);
      }
    });
  }, [isExpanded, progress]);

  // Blurring rather than collapsing outright, so the collapse still runs through the
  // empty-field rule below and a searched location stays on screen.
  useEffect(() => {
    fieldRef.current?.blur();
  }, [dismissSignal]);

  useEffect(() => {
    if (!clearSignal) {
      return;
    }

    fieldRef.current?.clear();
    setIsExpanded(false);
  }, [clearSignal]);

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => setAvailableWidth(event.nativeEvent.layout.width),
    []
  );

  const onTogglePress = useCallback(() => {
    if (isExpanded) {
      fieldRef.current?.focus();
      return;
    }

    setIsExpanded(true);
  }, [isExpanded]);

  // Only an empty field collapses on blur, so a location the guest searched for stays on screen.
  const onFieldBlur = useCallback((value: string) => {
    if (!value.trim()) {
      setIsExpanded(false);
    }
  }, []);

  // Grows leftwards out of the circle. Until the row is measured there is nothing to grow into.
  // Collapsed it is exactly the strip the circle covers, so it never shows past the circle.
  const fieldWidth = availableWidth
    ? progress.interpolate({
        inputRange: [0, 1],
        outputRange: [CIRCLE_OVERLAP, Math.max(availableWidth - CIRCLE_OVERHANG, CIRCLE_OVERLAP)],
      })
    : CIRCLE_OVERLAP;
  // The field's border and fill have to be gone by the time it is back behind the circle.
  const fieldOpacity = progress.interpolate({ inputRange: [0, 0.3, 1], outputRange: [0, 0, 1] });

  return (
    <View style={styles.row} onLayout={onLayout} pointerEvents="box-none">
      <Animated.View
        // Android hides TextInput text when any ancestor has an `opacity` style,
        // including an animated value that has already settled at 1.
        style={[
          styles.field,
          { width: fieldWidth },
          !(Platform.OS === 'android' && isExpanded) && { opacity: fieldOpacity },
        ]}
        pointerEvents={isExpanded ? 'auto' : 'none'}
      >
        {isFieldMounted && (
          <LocationSearchBar
            ref={fieldRef}
            autoFocus
            placeholder="Enter a location"
            inputContainerStyle={styles.fieldInput}
            dropdownStyle={styles.fieldDropdown}
            onBlur={onFieldBlur}
            onSelectLocation={onSelectLocation}
          />
        )}
      </Animated.View>

      <Pressable
        testID="store-locator-search-toggle"
        accessibilityLabel="Search for a location"
        dd-action-name="store locator open search"
        onPress={onTogglePress}
        borderRadius={999}
        style={styles.toggle}
      >
        <IconSearch size={28} color={tokens.colors.$blackOpacity75} />
      </Pressable>
    </View>
  );
};

const useMapSearchOverlayMqStyles = createMqStyles({
  // The field is a normal row child rather than another absolute layer, so the row keeps its
  // own height and the circle can be pinned over the field's right end.
  row: {
    $base: {
      alignItems: 'flex-start',
      flexDirection: 'row',
      height: CIRCLE_SIZE,
      justifyContent: 'flex-end',
      // Predictions hang below this 48pt row; Android otherwise clips them.
      overflow: 'visible',
      position: 'relative',
    },
  },
  // Stops short of the row's right edge so the circle hangs past the field, and carries no
  // shadow of its own: the design gives the field a stroke and the Card Shadow to the circle.
  field: {
    $base: {
      marginRight: CIRCLE_OVERHANG,
      overflow: 'visible',
    },
  },
  fieldInput: {
    $base: {
      // The circle sits on top of the field's right end, so the value must stop short of it.
      paddingRight: CIRCLE_OVERLAP,
    },
  },
  // The predictions hang below the circle rather than under it, so unlike the field they get
  // the full row width — the design runs them out to the same edge the map uses.
  fieldDropdown: {
    $base: {
      right: -CIRCLE_OVERHANG,
    },
  },
  toggle: {
    $base: {
      alignItems: 'center',
      backgroundColor: tokens.colors.$white,
      borderRadius: 999,
      elevation: 5,
      height: CIRCLE_SIZE,
      justifyContent: 'center',
      position: 'absolute',
      right: 0,
      shadowColor: tokens.colors.$black,
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.1,
      shadowRadius: 6,
      top: 0,
      width: CIRCLE_SIZE,
      zIndex: 2,
    },
  },
});
